import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Panel } from "@/components/panel";
import { StatCard } from "@/components/stat-card";
import { WorkbenchActionCard } from "@/components/workbench-action-card";
import { WORKBENCH_CONFIG } from "@/app/workbench-config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

interface ApiEntry {
  id: string;
  category: string;
  category_slug: string;
  name: string;
  url: string;
  description: string;
  auth: string;
  https: boolean;
  cors: string;
}

interface ApiCategory {
  name: string;
  slug: string;
  count: number;
}

interface ApiCatalog {
  generated_at: string;
  source: string;
  summary: Record<string, unknown>;
  categories: ApiCategory[];
  apis: ApiEntry[];
}

const AUTH_BADGES: Record<string, { label: string; cls: string }> = {
  none: { label: "Open", cls: "border-green-500/40 bg-green-500/10 text-green-400" },
  apiKey: { label: "API Key", cls: "border-brand-500/40 bg-brand-500/10 text-brand-300" },
  OAuth: { label: "OAuth", cls: "border-purple-500/40 bg-purple-500/10 text-purple-400" },
  "X-Mashape-Key": { label: "Mashape", cls: "border-brand-500/40 bg-brand-500/10 text-brand-300" },
  "User-Agent": { label: "User-Agent", cls: "border-brand-500/40 bg-brand-500/10 text-brand-300" },
};

const BADGE_BASE = "inline-flex rounded-full border px-2 py-0.5 text-[0.7rem] font-medium";

function AuthBadge({ auth }: { auth: string }) {
  const info = AUTH_BADGES[auth] ?? { label: auth || "Unknown", cls: "border-brand-500/40 bg-brand-500/10 text-brand-300" };
  return <span className={`${BADGE_BASE} ${info.cls}`}>{info.label}</span>;
}

function CorsBadge({ cors }: { cors: string }) {
  if (cors === "yes") return <span className={`${BADGE_BASE} border-green-500/40 bg-green-500/10 text-green-400`}>CORS</span>;
  if (cors === "no") return <span className={`${BADGE_BASE} border-red-500/40 bg-red-500/10 text-red-400`}>No CORS</span>;
  return <span className={`${BADGE_BASE} border-brand-500/40 bg-brand-500/10 text-brand-300`}>CORS?</span>;
}

export function IntegrationsPage() {
  const workbench = WORKBENCH_CONFIG["integrations"];
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [authFilter, setAuthFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const { data: catalog, isLoading } = useQuery<ApiCatalog>({
    queryKey: ["api-catalog"],
    queryFn: async () => {
      const res = await fetch("/catalog/public-apis.json");
      if (!res.ok) throw new Error("Failed to load API catalog");
      return res.json();
    },
    staleTime: Infinity,
  });

  const filtered = useMemo(() => {
    if (!catalog) return [];
    return catalog.apis.filter((api) => {
      if (selectedCategory !== "all" && api.category_slug !== selectedCategory) return false;
      if (authFilter === "open" && api.auth !== "none") return false;
      if (authFilter === "key" && api.auth === "none") return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          api.name.toLowerCase().includes(q) ||
          api.description.toLowerCase().includes(q) ||
          api.category.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [catalog, search, selectedCategory, authFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const visible = filtered.slice(page * pageSize, (page + 1) * pageSize);

  useEffect(() => setPage(0), [search, selectedCategory, authFilter]);

  const categoryStats = useMemo(() => {
    if (!catalog) return { total: 0, categories: 0, open: 0, https: 0 };
    return {
      total: catalog.apis.length,
      categories: catalog.categories.length,
      open: catalog.apis.filter((a) => a.auth === "none").length,
      https: catalog.apis.filter((a) => a.https).length,
    };
  }, [catalog]);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Provider status actions */}
      {workbench && (
        <Panel title={workbench.title} description={workbench.description}>
          <p className="text-sm text-muted">
            Check live backend model provider availability.
          </p>
        </Panel>
      )}

      {workbench && (
        <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
          {workbench.actions.map((action) => (
            <WorkbenchActionCard key={action.id} action={action} />
          ))}
        </div>
      )}

      {/* API Catalog */}
      <Panel
        title="Public API Directory"
        description={`${categoryStats.total.toLocaleString()} APIs across ${categoryStats.categories} categories`}
      >
        <div className="grid gap-2 md:gap-3 grid-cols-2 md:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]" style={{ marginBottom: "1.25rem" }}>
          <StatCard label="Total APIs" value={categoryStats.total.toLocaleString()} tone="warning" />
          <StatCard label="Categories" value={String(categoryStats.categories)} />
          <StatCard label="Open (no auth)" value={String(categoryStats.open)} tone="success" />
          <StatCard label="HTTPS" value={`${categoryStats.https} (${categoryStats.total ? Math.round((categoryStats.https / categoryStats.total) * 100) : 0}%)`} />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Input
            type="text"
            className="flex-1 min-w-[200px]"
            placeholder="Search APIs by name, description, or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select className="w-48" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
            <option value="all">All categories</option>
            {catalog?.categories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name} ({c.count})
              </option>
            ))}
          </Select>
          <Select className="w-36" value={authFilter} onChange={(e) => setAuthFilter(e.target.value)}>
            <option value="all">Any auth</option>
            <option value="open">Open only</option>
            <option value="key">Key required</option>
          </Select>
        </div>

        {/* Results info */}
        <div className="mb-3 text-xs text-muted">
          Showing {visible.length} of {filtered.length.toLocaleString()} APIs
          {totalPages > 1 && ` · Page ${page + 1} of ${totalPages}`}
        </div>

        {/* API Table */}
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted">Loading catalog...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>API</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Auth</TableHead>
                <TableHead>HTTPS</TableHead>
                <TableHead>CORS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((api) => (
                <TableRow key={api.id}>
                  <TableCell>
                    <a
                      href={api.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-300 hover:underline"
                    >
                      {api.name}
                    </a>
                    <div className="text-xs text-muted">{api.description}</div>
                  </TableCell>
                  <TableCell>
                    <button
                      className="text-xs text-brand-400 hover:underline"
                      onClick={() => setSelectedCategory(api.category_slug)}
                    >
                      {api.category}
                    </button>
                  </TableCell>
                  <TableCell><AuthBadge auth={api.auth} /></TableCell>
                  <TableCell>{api.https ? "✓" : "✗"}</TableCell>
                  <TableCell><CorsBadge cors={api.cors} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-4">
            <Button
              variant="ghost"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </Button>
            <span className="text-xs text-muted">
              Page {page + 1} / {totalPages}
            </span>
            <Button
              variant="ghost"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </Panel>
    </div>
  );
}
